import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, UiErrorCode } from '../../api/http';
import './StatePanel.css';

export type StatePanelVariant = 'loading' | 'empty' | 'error' | 'blocked';

export interface StatePanelAction {
  label: string;
  onClick?: () => void;
  to?: string;
  disabled?: boolean;
}

export interface StatePanelProps {
  /** kind/body are the v1 contract. variant/message stay as a short-lived
   * source-compatible bridge for already migrated screens. */
  kind?: StatePanelVariant;
  variant?: StatePanelVariant;
  title?: string;
  body?: string;
  message?: string;
  error?: ApiError | null;
  primaryAction?: StatePanelAction;
  secondaryAction?: StatePanelAction;
  compact?: boolean;
  /** Reserve assertive announcements for failures caused by the user's
   * immediate action. Initial and background load failures stay polite. */
  urgent?: boolean;
  children?: ReactNode;
  testId?: string;
}

const ERROR_COPY: Record<UiErrorCode, { title: string; message: string }> = {
  aborted: {
    title: 'That request was cancelled',
    message: 'Nothing else is needed unless you want to try again.',
  },
  offline: {
    title: 'You’re offline',
    message: 'Reconnect and try again when you’re ready.',
  },
  unauthorized: {
    title: 'That is no longer available',
    message: 'Return to your family account and try again.',
  },
  not_found: {
    title: 'That is no longer available',
    message: 'Your family details may have changed. Refresh to check.',
  },
  conflict: {
    title: 'A family setting changed',
    message: 'Refresh this view before trying again.',
  },
  rate_limited: {
    title: 'Let’s pause for a moment',
    message: 'The request limit was reached. Try again shortly.',
  },
  timeout: {
    title: 'This is taking longer than expected',
    message: 'Try again when you’re ready.',
  },
  service_unavailable: {
    title: 'Luma is having trouble',
    message: 'Try again in a moment.',
  },
  unknown: {
    title: 'We could not load this yet',
    message: 'Try again. Check this page before repeating the action.',
  },
};

/** Safe, user-facing copy for an API error code. Shared with the redesign
 * StateMessage adapter so the mapping lives in exactly one place. */
export function statePanelErrorCopy(error: ApiError | null | undefined): { title: string; message: string } | undefined {
  return error ? ERROR_COPY[error.code] : undefined;
}

const StatePanel: React.FC<StatePanelProps> = ({
  kind,
  variant: legacyVariant,
  title,
  body,
  message,
  error,
  primaryAction,
  secondaryAction,
  compact = false,
  urgent = false,
  children,
  testId,
}) => {
  const variant = kind || legacyVariant || 'empty';
  const copy = statePanelErrorCopy(error);
  const safeMessage = copy?.message || body || message;
  const role = variant === 'loading' || variant === 'error' ? (variant === 'error' && urgent ? 'alert' : 'status') : undefined;
  const live = variant === 'loading' || variant === 'error' ? (variant === 'error' && urgent ? 'assertive' : 'polite') : undefined;

  const action = (item: StatePanelAction | undefined, primary: boolean) => {
    if (!item) return null;
    const className = primary ? 'btn btn-primary' : 'btn btn-secondary';
    if (item.to) return <Link className={className} to={item.to}>{item.label}</Link>;
    return <button type="button" className={className} onClick={item.onClick} disabled={item.disabled}>{item.label}</button>;
  };

  return (
    <section
      className={`state-panel state-panel--${variant}${compact ? ' state-panel--compact' : ''}`}
      role={role}
      aria-live={live}
      aria-busy={variant === 'loading'}
      data-testid={testId}
    >
      {variant === 'loading' && <span className="state-panel__spinner" aria-hidden="true" />}
      <div className="state-panel__copy">
        <h2 className="state-panel__title">{title || copy?.title || (variant === 'empty' ? 'Nothing here yet' : 'A quiet moment')}</h2>
        {safeMessage && <p className="state-panel__message">{safeMessage}</p>}
        {children}
      </div>
      {(primaryAction || secondaryAction) && (
        <div className="state-panel__actions">
          {action(primaryAction, true)}
          {action(secondaryAction, false)}
        </div>
      )}
    </section>
  );
};

export default StatePanel;
